import React, { ReactNode } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { UseFormReturn } from 'react-hook-form';
import { createStyleSheet, UnistylesTheme, useStyles } from 'react-native-unistyles';
import { Slider } from '@miblanchard/react-native-slider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../../services/translate';
import { Manufacturer } from '../../../schemas/crm';
import Card from '../../../components/Card';
import Divider from '../../../components/Divider';
import FormInput from '../../../components/FormInput';
import FormDropDownPicker from '../../../components/FormDropDownPicker';
import MyAppText from '../../../components/MyAppText';

export const VANDALISM_CONFIG: Record<Manufacturer, VandalismConfiguration> = {
    hikvision: {
        sceneChange: { minSensitivity: 0, maxSensitivity: 100 },
        defocus: { minSensitivity: 0, maxSensitivity: 100 },
    },
    dahua: {
        videoTampering: { minSensitivity: 0, maxSensitivity: 6 },
        sceneChange: { minSensitivity: 0, maxSensitivity: 5 },
        defocus: { minSensitivity: 0, maxSensitivity: 100 },
    },
    intelbras: {},
};

export type SensitivityField = 'videoTamperingSensitivity' | 'sceneChangeSensitivity' | 'defocusSensitivity';

export interface SynchronizationConfigurationFormFields {
    videoTamperingSensitivity: string;
    sceneChangeSensitivity: string;
    defocusSensitivity: string;
    overlayText: string;
    frameRate: string;
    videoBitRate: string;
    videoCodec: CameraVideoCodec | '';
    resolution: CameraResolution | '';
    lprWebhookUrl: string;
    facialWebhookUrl: string;
    areaInvasionWebhookUrl: string;
    ddns4Url: string;
    ddns6Url: string;
    createRtspWatcher: '' | 'true' | 'false';
    adminPasswordRotation: '' | 'true' | 'false';
}

export const DEFAULT_SYNCHRONIZATION_CONFIGURATION: SynchronizationConfigurationFormFields = {
    videoTamperingSensitivity: '0',
    sceneChangeSensitivity: '0',
    defocusSensitivity: '0',
    overlayText: '',
    frameRate: '',
    videoBitRate: '',
    videoCodec: '',
    resolution: '',
    lprWebhookUrl: '',
    facialWebhookUrl: '',
    areaInvasionWebhookUrl: '',
    ddns4Url: '',
    ddns6Url: '',
    createRtspWatcher: '',
    adminPasswordRotation: '',
};

export function isValidWebhookUrl(value: string) {
    const trimmedValue = value.trim();

    if (!/^https?:\/\//i.test(trimmedValue)) {
        return false;
    }

    try {
        return new URL(trimmedValue).hostname.replace(/^\[|\]$/g, '').length > 0;
    } catch {
        return false;
    }
}

export function getDefaultSensitivities(manufacturer: Manufacturer): Pick<SynchronizationConfigurationFormFields, SensitivityField> {
    const configs: Partial<Record<Manufacturer, Pick<SynchronizationConfigurationFormFields, SensitivityField>>> = {
        hikvision: {
            videoTamperingSensitivity: '0',
            sceneChangeSensitivity: '55',
            defocusSensitivity: '55',
        },
        dahua: {
            videoTamperingSensitivity: '5',
            sceneChangeSensitivity: '5',
            defocusSensitivity: '70',
        },
    };

    return configs[manufacturer] ?? {
        videoTamperingSensitivity: '0',
        sceneChangeSensitivity: '0',
        defocusSensitivity: '0',
    };
}

const EMPTY_OPTION_LABEL = '-';

const SENSITIVITY_FIELDS: { field: SensitivityField; configKey: keyof VandalismConfiguration; }[] = [
    { field: 'videoTamperingSensitivity', configKey: 'videoTampering' },
    { field: 'sceneChangeSensitivity', configKey: 'sceneChange' },
    { field: 'defocusSensitivity', configKey: 'defocus' },
];

export function buildSynchronizationConfigurationPayload(
    values: SynchronizationConfigurationFormFields,
    manufacturer: Manufacturer
): UpdateCameraSynchronizationConfigurationParameters {
    const vandalismConfig = VANDALISM_CONFIG[manufacturer] ?? {};

    const sensitivity = (field: SensitivityField, configKey: keyof VandalismConfiguration) =>
        vandalismConfig[configKey] ? String(Number(values[field] ?? 0)) : null;

    const text = (value: string | undefined) => {
        const trimmedValue = (value ?? '').trim();
        return trimmedValue ? trimmedValue : null;
    };

    const number = (value: string | undefined) => {
        const trimmedValue = (value ?? '').trim();
        return trimmedValue ? Number(trimmedValue) : null;
    };

    const boolean = (value: string | undefined) => value ? value === 'true' : null;

    return {
        manufacturer,
        videoTamperingSensitivity: sensitivity('videoTamperingSensitivity', 'videoTampering'),
        sceneChangeSensitivity: sensitivity('sceneChangeSensitivity', 'sceneChange'),
        defocusSensitivity: sensitivity('defocusSensitivity', 'defocus'),
        overlayText: text(values.overlayText),
        frameRate: number(values.frameRate),
        videoBitRate: number(values.videoBitRate),
        videoCodec: values.videoCodec || null,
        resolution: values.resolution || null,
        lprWebhookUrl: text(values.lprWebhookUrl),
        facialWebhookUrl: text(values.facialWebhookUrl),
        areaInvasionWebhookUrl: text(values.areaInvasionWebhookUrl),
        ddns4Url: text(values.ddns4Url),
        ddns6Url: text(values.ddns6Url),
        createRtspWatcher: boolean(values.createRtspWatcher),
        adminPasswordRotation: boolean(values.adminPasswordRotation),
    };
}

export function buildSynchronizationConfigurationFormValues(
    data: CameraSynchronizationConfiguration,
    manufacturer: Manufacturer
): SynchronizationConfigurationFormFields {
    const { configuration } = data;
    const defaultSensitivities = getDefaultSensitivities(manufacturer);

    const boolean = (value: boolean | null) => value === null ? '' : (value ? 'true' : 'false');

    return {
        videoTamperingSensitivity: configuration.videoTamperingSensitivity ?? defaultSensitivities.videoTamperingSensitivity,
        sceneChangeSensitivity: configuration.sceneChangeSensitivity ?? defaultSensitivities.sceneChangeSensitivity,
        defocusSensitivity: configuration.defocusSensitivity ?? defaultSensitivities.defocusSensitivity,
        overlayText: configuration.overlayText ?? '',
        frameRate: configuration.frameRate !== null ? String(configuration.frameRate) : '',
        videoBitRate: configuration.videoBitRate !== null ? String(configuration.videoBitRate) : '',
        videoCodec: configuration.videoCodec ?? '',
        resolution: configuration.resolution ?? '',
        lprWebhookUrl: configuration.lprWebhookUrl ?? '',
        facialWebhookUrl: configuration.facialWebhookUrl ?? '',
        areaInvasionWebhookUrl: configuration.areaInvasionWebhookUrl ?? '',
        ddns4Url: configuration.ddns4Url ?? '',
        ddns6Url: configuration.ddns6Url ?? '',
        createRtspWatcher: boolean(configuration.createRtspWatcher),
        adminPasswordRotation: boolean(configuration.adminPasswordRotation),
    };
}

export function isSynchronizationConfigurationValid(
    values: SynchronizationConfigurationFormFields | undefined,
    manufacturer: Manufacturer | undefined
): boolean {
    if (!values || !manufacturer) {
        return false;
    }

    const vandalismConfig = VANDALISM_CONFIG[manufacturer] ?? {};

    for (const { field, configKey } of SENSITIVITY_FIELDS) {
        const bounds = vandalismConfig[configKey];
        if (!bounds) {
            continue;
        }

        const value = Number(values[field]);
        if (!Number.isInteger(value) || value < bounds.minSensitivity || value > bounds.maxSensitivity) {
            return false;
        }
    }

    if ((values.overlayText ?? '').length > 44) {
        return false;
    }

    if (values.frameRate?.trim()) {
        const frameRate = Number(values.frameRate);
        if (!Number.isInteger(frameRate) || frameRate < 1 || frameRate > 60) {
            return false;
        }
    }

    if (values.videoBitRate?.trim()) {
        const videoBitRate = Number(values.videoBitRate);
        if (!Number.isInteger(videoBitRate) || videoBitRate < 128 || videoBitRate > 16384) {
            return false;
        }
    }

    const urlFields = ['lprWebhookUrl', 'facialWebhookUrl', 'areaInvasionWebhookUrl', 'ddns4Url', 'ddns6Url'] as const;
    for (const field of urlFields) {
        const value = String(values[field] ?? '');
        if (value.trim() && !isValidWebhookUrl(value)) {
            return false;
        }
    }

    return true;
}

function SectionHeader({ title, description, children, viewZIndex, childrenZIndex }: {
    title: string;
    description?: string;
    children: ReactNode;
    viewZIndex?: number;
    childrenZIndex?: number;
}) {
    const { styles, theme } = useStyles(styleSheet);

    return <View style={{ flexDirection: 'column', rowGap: 16, zIndex: viewZIndex }}>
        <View style={{ flexDirection: 'column', rowGap: 8, zIndex: 2 }}>
            <MyAppText style={styles.sectionHeaderTitle}>{title}</MyAppText>
            {description ? <MyAppText style={styles.description}>{description}</MyAppText> : null}
            <Divider style={{ zIndex: -1 }} color={theme.colors.primary['200']} />
        </View>
        <View style={{ zIndex: childrenZIndex ?? 1, rowGap: 16, columnGap: 4 }}>
            {children}
        </View>
    </View>;
}

function SensitivitySlider({ label, max, value, onValueChange, disabled, theme }: {
    label: string;
    max: number;
    value: number;
    onValueChange: (v: number) => void;
    disabled: boolean;
    theme: UnistylesTheme;
}) {
    return (
        <View style={{ maxWidth: 320, minWidth: 240, flexGrow: 1 }}>
            <MyAppText style={{ fontSize: 14 }}>{label}</MyAppText>
            <Slider
                animateTransitions
                minimumValue={0}
                maximumValue={max}
                minimumTrackTintColor={theme.colors.neutral['400']}
                thumbTintColor={theme.colors.neutral['400']}
                maximumTrackTintColor={theme.colors.neutral['300']}
                value={value}
                step={1}
                onValueChange={([v]: number[]) => onValueChange(v)}
                disabled={disabled}
            />
            <View style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                <MyAppText style={{ fontWeight: 'bold' }}>{value.toFixed(0)}</MyAppText>
            </View>
        </View>
    );
}

function CompanyDefaultHint({ value }: { value: string; }) {
    const { styles } = useStyles(styleSheet);

    return <MyAppText style={styles.hint}>
        {`${translate('companyDefault')}: ${value || '-'}`}
    </MyAppText>;
}

interface SynchronizationSettingsTabProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    values?: SynchronizationConfigurationFormFields;
    companyDefaults?: UpdateCompanyCameraConfigurationParameters;
    manufacturer?: Manufacturer;
    disabled: boolean;
    isLoading: boolean;
}

export default function SynchronizationSettingsTab({
    form,
    values,
    companyDefaults,
    manufacturer,
    disabled,
    isLoading,
}: SynchronizationSettingsTabProps) {
    const { styles, theme } = useStyles(styleSheet);

    const vandalismConfig = manufacturer ? VANDALISM_CONFIG[manufacturer] ?? {} : {};
    const showOverrides = !!companyDefaults;

    function sliderValue(field: SensitivityField): number {
        return Number(values?.[field] ?? 0);
    }

    function booleanLabel(value: boolean | undefined) {
        return value ? translate('yes') : translate('no');
    }

    function withEmptyOption(items: { label: string; value: string; }[]) {
        return [{ label: EMPTY_OPTION_LABEL, value: '' }, ...items];
    }

    return <Card>
        <ScrollView style={{ padding: 24 }} contentContainerStyle={{ rowGap: 32 }}>
            {isLoading && <ActivityIndicator size='small' color={theme.colors.loadingColor} />}

            <View style={styles.warningBox}>
                <FontAwesomeIcon icon={faInfoCircle} color={theme.colors.iconColor} />
                <View style={{ flexShrink: 1, rowGap: 4 }}>
                    <MyAppText style={styles.warningText}>{translate('synchronizationSettingsManufacturerWarning')}</MyAppText>
                    {showOverrides
                        ? <MyAppText style={styles.warningText}>{translate('emptyFieldUsesCompanyDefault')}</MyAppText>
                        : null}
                </View>
            </View>

            <SectionHeader
                title={translate('vandalismConfigurations')}
                description={translate('vandalismConfigurationsDescription')}
                viewZIndex={1}
            >
                {Object.keys(vandalismConfig).length === 0
                    ? <MyAppText style={styles.description}>{translate('manufacturerWithoutVandalismConfigurations')}</MyAppText>
                    : <View style={styles.fieldsRow}>
                        {vandalismConfig.videoTampering && <SensitivitySlider
                            label={translate('videoTamperingSensitivity')}
                            max={vandalismConfig.videoTampering.maxSensitivity}
                            value={sliderValue('videoTamperingSensitivity')}
                            onValueChange={v => form.setValue('synchronizationConfiguration.videoTamperingSensitivity', String(v), { shouldDirty: true })}
                            disabled={disabled}
                            theme={theme}
                        />}
                        {vandalismConfig.sceneChange && <SensitivitySlider
                            label={translate('sceneChangeSensitivity')}
                            max={vandalismConfig.sceneChange.maxSensitivity}
                            value={sliderValue('sceneChangeSensitivity')}
                            onValueChange={v => form.setValue('synchronizationConfiguration.sceneChangeSensitivity', String(v), { shouldDirty: true })}
                            disabled={disabled}
                            theme={theme}
                        />}
                        {vandalismConfig.defocus && <SensitivitySlider
                            label={translate('defocusSensitivity')}
                            max={vandalismConfig.defocus.maxSensitivity}
                            value={sliderValue('defocusSensitivity')}
                            onValueChange={v => form.setValue('synchronizationConfiguration.defocusSensitivity', String(v), { shouldDirty: true })}
                            disabled={disabled}
                            theme={theme}
                        />}
                    </View>
                }
            </SectionHeader>

            {!showOverrides
                ? <MyAppText style={styles.description}>{translate('failedToLoadSynchronizationSettings')}</MyAppText>
                : <>
                    <SectionHeader title={translate('cameraConfigurations')} viewZIndex={4} childrenZIndex={4}>
                        <View style={styles.fieldsRow}>
                            <View style={styles.field}>
                                <FormInput
                                    label={translate('watermark')}
                                    placeholder={companyDefaults.overlayText || translate('watermark')}
                                    form={form}
                                    fieldName='synchronizationConfiguration.overlayText'
                                    disabled={disabled}
                                    maxLength={44}
                                />
                                <CompanyDefaultHint value={companyDefaults.overlayText} />
                            </View>
                            <View style={styles.field}>
                                <FormInput
                                    label={translate('fps')}
                                    placeholder={String(companyDefaults.frameRate)}
                                    form={form}
                                    fieldName='synchronizationConfiguration.frameRate'
                                    mode='NUMBER'
                                    minValue={1}
                                    maxValue={60}
                                    disabled={disabled}
                                />
                                <CompanyDefaultHint value={String(companyDefaults.frameRate)} />
                            </View>
                            <View style={styles.field}>
                                <FormInput
                                    label={translate('videoBitRate')}
                                    placeholder={String(companyDefaults.videoBitRate)}
                                    form={form}
                                    fieldName='synchronizationConfiguration.videoBitRate'
                                    mode='NUMBER'
                                    minValue={128}
                                    maxValue={16384}
                                    disabled={disabled}
                                />
                                <CompanyDefaultHint value={String(companyDefaults.videoBitRate)} />
                            </View>
                        </View>
                        <View style={[styles.fieldsRow, { zIndex: 4 }]}>
                            <View style={styles.field}>
                                <FormDropDownPicker
                                    label={translate('videoCodec')}
                                    items={withEmptyOption([
                                        { label: 'H.264', value: 'h.264' },
                                        { label: 'H.265', value: 'h.265' },
                                    ])}
                                    form={form}
                                    fieldName='synchronizationConfiguration.videoCodec'
                                    placeholder={EMPTY_OPTION_LABEL}
                                    searchable={false}
                                    zIndex={5}
                                    disabled={disabled}
                                />
                                <CompanyDefaultHint value={companyDefaults.videoCodec} />
                            </View>
                            <View style={styles.field}>
                                <FormDropDownPicker
                                    label={translate('resolution')}
                                    items={withEmptyOption([
                                        { label: '1280x720', value: '1280x720' },
                                        { label: '1920x1080', value: '1920x1080' },
                                        { label: '2560x1440', value: '2560x1440' },
                                        { label: '3840x2160', value: '3840x2160' },
                                    ])}
                                    form={form}
                                    fieldName='synchronizationConfiguration.resolution'
                                    placeholder={EMPTY_OPTION_LABEL}
                                    searchable={false}
                                    zIndex={4}
                                    disabled={disabled}
                                />
                                <CompanyDefaultHint value={companyDefaults.resolution} />
                            </View>
                        </View>
                    </SectionHeader>

                    <SectionHeader title={translate('webhookUrls')} viewZIndex={3}>
                        <View style={styles.fieldsRow}>
                            <View style={styles.field}>
                                <FormInput
                                    label={translate('lprWebhookUrl')}
                                    placeholder={companyDefaults.lprWebhookUrl}
                                    form={form}
                                    fieldName='synchronizationConfiguration.lprWebhookUrl'
                                    disabled={disabled}
                                />
                                <CompanyDefaultHint value={companyDefaults.lprWebhookUrl} />
                            </View>
                            <View style={styles.field}>
                                <FormInput
                                    label={translate('facialWebhookUrl')}
                                    placeholder={companyDefaults.facialWebhookUrl}
                                    form={form}
                                    fieldName='synchronizationConfiguration.facialWebhookUrl'
                                    disabled={disabled}
                                />
                                <CompanyDefaultHint value={companyDefaults.facialWebhookUrl} />
                            </View>
                            <View style={styles.field}>
                                <FormInput
                                    label={translate('areaInvasionWebhookUrl')}
                                    placeholder={companyDefaults.areaInvasionWebhookUrl}
                                    form={form}
                                    fieldName='synchronizationConfiguration.areaInvasionWebhookUrl'
                                    disabled={disabled}
                                />
                                <CompanyDefaultHint value={companyDefaults.areaInvasionWebhookUrl} />
                            </View>
                        </View>
                    </SectionHeader>

                    <SectionHeader title={translate('ddnsUrls')} viewZIndex={2}>
                        <View style={styles.fieldsRow}>
                            <View style={styles.field}>
                                <FormInput
                                    label={translate('ddns4Url')}
                                    placeholder={companyDefaults.ddns4Url}
                                    form={form}
                                    fieldName='synchronizationConfiguration.ddns4Url'
                                    disabled={disabled}
                                />
                                <CompanyDefaultHint value={companyDefaults.ddns4Url} />
                            </View>
                            <View style={styles.field}>
                                <FormInput
                                    label={translate('ddns6Url')}
                                    placeholder={companyDefaults.ddns6Url}
                                    form={form}
                                    fieldName='synchronizationConfiguration.ddns6Url'
                                    disabled={disabled}
                                />
                                <CompanyDefaultHint value={companyDefaults.ddns6Url} />
                            </View>
                        </View>
                    </SectionHeader>

                    <SectionHeader title={translate('generalConfigurations')} viewZIndex={5} childrenZIndex={5}>
                        <View style={styles.fieldsRow}>
                            <View style={styles.field}>
                                <FormDropDownPicker
                                    label={translate('createRtspWatcher')}
                                    items={withEmptyOption([
                                        { label: translate('yes'), value: 'true' },
                                        { label: translate('no'), value: 'false' },
                                    ])}
                                    form={form}
                                    fieldName='synchronizationConfiguration.createRtspWatcher'
                                    placeholder={EMPTY_OPTION_LABEL}
                                    searchable={false}
                                    zIndex={6}
                                    disabled={disabled}
                                />
                                <CompanyDefaultHint value={booleanLabel(companyDefaults.createRtspWatcher)} />
                            </View>
                            <View style={styles.field}>
                                <FormDropDownPicker
                                    label={translate('adminPasswordRotation')}
                                    items={withEmptyOption([
                                        { label: translate('yes'), value: 'true' },
                                        { label: translate('no'), value: 'false' },
                                    ])}
                                    form={form}
                                    fieldName='synchronizationConfiguration.adminPasswordRotation'
                                    placeholder={EMPTY_OPTION_LABEL}
                                    searchable={false}
                                    zIndex={5}
                                    disabled={disabled}
                                />
                                <CompanyDefaultHint value={booleanLabel(companyDefaults.adminPasswordRotation)} />
                            </View>
                        </View>
                    </SectionHeader>
                </>
            }
        </ScrollView>
    </Card>;
}

const styleSheet = createStyleSheet(theme => ({
    sectionHeaderTitle: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16,
    },
    fieldsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        columnGap: 24,
        rowGap: 16,
    },
    field: {
        maxWidth: 320,
        minWidth: 240,
        flexGrow: 1,
        rowGap: 4,
    },
    description: {
        color: theme.colors.labelColor,
        fontSize: 12,
    },
    hint: {
        color: theme.colors.labelColor,
        fontSize: 11,
    },
    warningBox: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 8,
        padding: 12,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.primary['200'],
    },
    warningText: {
        fontSize: 12,
        flexShrink: 1,
    },
}));
